<template>
  <div ref="rootEl" class="wrap">
    <button
      type="button"
      class="trigger"
      :class="{ active: open || activeCount > 0 }"
      :aria-expanded="open"
      aria-haspopup="dialog"
      title="Filters"
      @click="open = !open"
    >
      <AppIcon name="filter" :size="15" />
      <span v-if="activeCount > 0" class="badge">{{ activeCount }}</span>
    </button>

    <div v-if="open" class="panel" role="dialog" aria-label="Filters">
      <div class="panelHead">
        <span class="panelTitle">Filters</span>
        <button v-if="activeCount > 0" type="button" class="clearAll" @click="$emit('clear')">
          Clear all
        </button>
      </div>

      <label class="field">
        <span class="fieldLabel">Recipient</span>
        <input
          class="input"
          :value="search"
          placeholder="Search recipient…"
          @input="$emit('update:search', ($event.target as HTMLInputElement).value)"
        />
      </label>

      <label class="field">
        <span class="fieldLabel">Owner</span>
        <SearchSelect
          :modelValue="owner"
          :options="ownerOptions"
          placeholder="All owners"
          search-placeholder="Search owners…"
          clear-label="All owners"
          @update:modelValue="(v: string) => $emit('update:owner', v || '')"
        />
      </label>

      <label class="field">
        <span class="fieldLabel">Account</span>
        <SearchSelect
          :modelValue="account"
          :optionsFunction="accountOptionsFn"
          :maxResults="20"
          placeholder="All accounts"
          search-placeholder="Search accounts…"
          clear-label="All accounts"
          @update:modelValue="(v: string) => $emit('update:account', v || '')"
        />
      </label>

      <label class="field">
        <span class="fieldLabel">Sales play</span>
        <SearchSelect
          :modelValue="play"
          :options="playOptions"
          placeholder="All plays"
          search-placeholder="Search plays…"
          clear-label="All plays"
          @update:modelValue="(v: string) => $emit('update:play', v || '')"
        />
      </label>

      <label class="field">
        <span class="fieldLabel">Sequence step</span>
        <input
          class="input"
          type="number"
          min="1"
          :value="step"
          placeholder="Any step"
          @input="onStepInput"
        />
      </label>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import AppIcon from '../../../components/AppIcon.vue'
import SearchSelect from '../../../components/SearchSelect.vue'

/**
 * The secondary filters (recipient / account / play / owner), collapsed behind
 * one icon so the filter row can share a line with the type cards.
 *
 * Stage is deliberately NOT in here — it stays visible on the toolbar. It's the
 * filter that changes what the page fundamentally shows, and hiding "To Do"
 * behind a popover would make the default view look unfiltered.
 *
 * The badge counts applied filters so nothing is silently narrowing the list
 * while the panel is shut.
 */

defineProps<{
  search: string
  account: string
  play: string
  owner: string
  step: string
  playOptions: { value: string; label: string }[]
  ownerOptions: { value: string; label: string }[]
  accountOptionsFn: (q: string) => Promise<any>
  activeCount: number
}>()

const emit = defineEmits<{
  'update:search': [string]
  'update:account': [string]
  'update:play': [string]
  'update:owner': [string]
  'update:step': [string]
  clear: []
}>()

const open = ref(false)

/** Out-of-range clears the filter — the server rejects anything below 1. */
function onStepInput(e: Event) {
  const n = Number.parseInt((e.target as HTMLInputElement).value, 10)
  emit('update:step', n >= 1 ? String(n) : '')
}
const rootEl = ref<HTMLElement | null>(null)

/**
 * Close on outside pointerdown, but ignore clicks that land inside a
 * SearchSelect's own dropdown — those render in this subtree, so containment is
 * enough; the guard is for the rest of the page.
 */
function onPointerDown(e: PointerEvent) {
  if (!open.value) return
  const t = e.target
  if (t instanceof Node && rootEl.value && !rootEl.value.contains(t)) open.value = false
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && open.value) open.value = false
}

onMounted(() => {
  document.addEventListener('pointerdown', onPointerDown)
  document.addEventListener('keydown', onKeydown)
})
onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onPointerDown)
  document.removeEventListener('keydown', onKeydown)
})
</script>

<style scoped>
.wrap { position: relative; display: inline-flex; flex-shrink: 0; }

.trigger {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-1);
  width: 34px;
  height: 34px;
  padding: 0;
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-md);
  background: var(--color-bg-surface);
  color: var(--color-text-secondary);
  cursor: pointer;
}
.trigger:hover { color: var(--color-text-primary); border-color: var(--color-border-strong); }
.trigger.active { color: var(--color-accent); border-color: var(--color-accent); }

.badge {
  position: absolute;
  top: -5px;
  right: -5px;
  min-width: 15px;
  height: 15px;
  padding: 0 3px;
  border-radius: var(--radius-full);
  background: var(--color-accent);
  color: #fff;
  font-size: 10px;
  font-weight: var(--fw-semibold);
  line-height: 15px;
  text-align: center;
}

.panel {
  position: absolute;
  top: calc(100% + 6px);
  /* Opens down-and-left: the trigger is the rightmost control on the row, so a
     left-anchored panel would extend under the context drawer. */
  right: 0;
  z-index: 40;
  width: 260px;
  padding: var(--space-3);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  background: var(--color-bg-surface);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md, 0 8px 24px rgba(0, 0, 0, 0.12));
}

.panelHead { display: flex; align-items: center; justify-content: space-between; }
.panelTitle {
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--tracking-wide);
  text-transform: uppercase;
  color: var(--color-text-tertiary);
}
.clearAll {
  border: none;
  background: none;
  padding: 0;
  font-family: inherit;
  font-size: var(--fs-xs);
  color: var(--color-accent);
  cursor: pointer;
}
.clearAll:hover { text-decoration: underline; }

.field { display: flex; flex-direction: column; gap: var(--space-1); }
.fieldLabel { font-size: var(--fs-xs); color: var(--color-text-tertiary); }
.field .input { width: 100%; }
</style>
